import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DataTable, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Badge,
  toast,
  TextArea,
  Select,
} from '@etherisc/ui-kit';
import { Eye, RotateCcw, RefreshCw } from 'lucide-react';
import { useLedgerJournals } from '../../hooks/ledger/useLedgerJournals';
import { PermissionGate } from '../../components/PermissionGate';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { Modal } from '../../components/ledger/Modal';
import { ClientSelect } from '../../components/insurance/ClientSelect';
import { CostCenterSelect } from '../../components/insurance/CostCenterSelect';

export function JournalManagement() {
  const navigate = useNavigate();
  const [reversalDialog, setReversalDialog] = useState<{
    isOpen: boolean;
    journalId: string | null;
    journalDescription: string;
  }>({
    isOpen: false,
    journalId: null,
    journalDescription: '',
  });
  const [reversalReason, setReversalReason] = useState('');
  const [isReversing, setIsReversing] = useState(false);

  // Insurance filtering state
  const [insuranceFilters, setInsuranceFilters] = useState({
    clientId: '',
    costCenterId: '',
    businessEventType: '',
    currency: '',
    hasClientDimensions: false
  });

  const {
    journals,
    loading,
    error,
    filters,
    setFilters,
    total,
    refreshJournals,
    reverseJournal,
  } = useLedgerJournals();

  // Handle journal reversal
  const handleReverseJournal = useCallback(async () => {
    if (!reversalDialog.journalId || !reversalReason.trim()) return;

    setIsReversing(true);
    try {
      await reverseJournal(reversalDialog.journalId, reversalReason.trim());
      toast.success('Journal reversed successfully');
      setReversalDialog({ isOpen: false, journalId: null, journalDescription: '' });
      setReversalReason('');
    } catch (error) {
      toast.error('Failed to reverse journal', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsReversing(false);
    }
  }, [reversalDialog.journalId, reversalReason, reverseJournal]);

  // Table columns
  const columns = [
    {
      accessorKey: 'externalRef',
      header: 'External Ref',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { id: string; externalRef: string };
        return (
          <Button
            variant="link"
            className="p-0 h-auto font-mono text-sm"
            onClick={() => navigate(`/ledger/journals/${journal.id}`)}
          >
            {journal.externalRef}
          </Button>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { description: string };
        return (
          <div className="max-w-xs truncate" title={journal.description}>
            {journal.description}
          </div>
        );
      },
    },
    {
      accessorKey: 'journalDate',
      header: 'Date',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { journalDate: string };
        return formatDate(journal.journalDate);
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { status: string };
        const status = journal.status;
        const variant = status === 'POSTED' ? 'success' : 
                       status === 'REVERSED' ? 'destructive' : 'secondary';
        return <Badge variant={variant}>{status}</Badge>;
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Amount',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { totalAmount: string };
        return formatCurrency(parseFloat(journal.totalAmount));
      },
    },
    {
      accessorKey: 'postedAt',
      header: 'Posted At',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { postedAt?: string };
        return journal.postedAt ? formatDate(journal.postedAt) : '-';
      },
    },
    // Insurance dimensions (conditionally shown)
    {
      accessorKey: 'clientInfo',
      header: 'Client',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { lines?: Array<{ clientId?: string }> };
        // Check if journal has insurance dimensions
        const hasClientData = journal.lines?.some((line) => line.clientId);
        
        if (!hasClientData) {
          return <span className="text-muted-foreground">—</span>;
        }

        const clientLine = journal.lines?.find((line) => line.clientId) as { clientId?: string; costCenterId?: string } | undefined;
        return (
          <div className="text-sm">
            <div className="font-medium">{clientLine?.clientId?.substring(0, 8)}...</div>
            {clientLine?.costCenterId && (
              <div className="text-muted-foreground">{clientLine.costCenterId.substring(0, 8)}...</div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'eventType',
      header: 'Event Type',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { lines?: Array<{ businessEventType?: string }> };
        const eventTypes = journal.lines?.map((line) => line.businessEventType).filter(Boolean);
        const uniqueEvents = [...new Set(eventTypes)];
        
        if (uniqueEvents.length === 0) {
          return <span className="text-muted-foreground">—</span>;
        }

        return (
          <div className="text-sm">
            {uniqueEvents.map((event, index) => (
              <Badge key={index} variant="outline" className="mr-1 mb-1">
                {String(event)}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { lines?: Array<{ currency?: string }> };
        const currencies = journal.lines?.map((line) => line.currency).filter(Boolean);
        const uniqueCurrencies = [...new Set(currencies)];
        
        if (uniqueCurrencies.length === 0) {
          return <span className="text-muted-foreground">—</span>;
        }

        return (
          <div className="text-sm font-mono">
            {uniqueCurrencies.join(', ')}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: { row: { original: unknown } }) => {
        const journal = row.original as { id: string; status: string; description: string };
        return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ledger/journals/${journal.id}`)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <PermissionGate permission="ledger:reverse">
            {journal.status === 'POSTED' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReversalDialog({
                  isOpen: true,
                  journalId: journal.id,
                  journalDescription: journal.description,
                })}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </PermissionGate>
        </div>
        );
      },
    },
  ];

  // Filter handlers
  const handleStatusFilter = useCallback((status: string) => {
    setFilters({
      ...filters,
      status: status === 'all' ? undefined : status as 'DRAFT' | 'POSTED' | 'REVERSED',
    });
  }, [filters, setFilters]);

  const handlePeriodFilter = useCallback((periodName: string) => {
    setFilters({
      ...filters,
      periodName: periodName === 'all' ? undefined : periodName,
    });
  }, [filters, setFilters]);

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading journals: {error}</p>
              <Button onClick={refreshJournals} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Journal Management</h1>
          <p className="text-muted-foreground">
            View and manage accounting journal entries
          </p>
        </div>
        <Button onClick={refreshJournals} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Journals ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="space-y-4 mb-6">
            {/* Basic Filters */}
            <div className="flex gap-4">
              <Select
                label="Status"
                value={filters.status || 'all'}
                onValueChange={(value: string) => handleStatusFilter(value)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'POSTED', label: 'Posted' },
                  { value: 'REVERSED', label: 'Reversed' },
                ]}
              />
              <Select
                label="Period"
                value={filters.periodName || 'all'}
                onValueChange={(value: string) => handlePeriodFilter(value)}
                options={[
                  { value: 'all', label: 'All Periods' },
                  { value: '2024-Q1', label: '2024-Q1' },
                  { value: '2024-Q2', label: '2024-Q2' },
                ]}
              />
            </div>

            {/* Insurance Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="col-span-full text-sm font-medium text-gray-700 mb-2">Insurance Filters</h3>
              
              <ClientSelect
                label="Client"
                value={insuranceFilters.clientId}
                onChange={(clientId) => setInsuranceFilters(prev => ({ ...prev, clientId }))}
                placeholder="All clients"
              />

              <CostCenterSelect
                label="Policy/Cost Center"
                value={insuranceFilters.costCenterId}
                onChange={(costCenterId) => setInsuranceFilters(prev => ({ ...prev, costCenterId }))}
                placeholder="All policies"
              />

              <Select
                label="Event Type"
                value={insuranceFilters.businessEventType || 'none'}
                onValueChange={(businessEventType) => setInsuranceFilters(prev => ({ ...prev, businessEventType: businessEventType === 'none' ? '' : businessEventType }))}
                options={[
                  { value: 'none', label: 'All Events' },
                  { value: 'PREMIUM_DUE', label: 'Premium Due' },
                  { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
                  { value: 'PREMIUM_EARNED', label: 'Premium Earned' },
                  { value: 'COMMISSION_DUE', label: 'Commission Due' },
                  { value: 'COMMISSION_PAID', label: 'Commission Paid' },
                  { value: 'BATCH_TRANSFER', label: 'Batch Transfer' }
                ]}
              />

              <Select
                label="Currency"
                value={insuranceFilters.currency || 'none'}
                onValueChange={(currency) => setInsuranceFilters(prev => ({ ...prev, currency: currency === 'none' ? '' : currency }))}
                options={[
                  { value: 'none', label: 'All Currencies' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'USD', label: 'USD' },
                  { value: 'GBP', label: 'GBP' }
                ]}
              />

              {/* Filter Actions */}
              <div className="col-span-full flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInsuranceFilters({
                    clientId: '',
                    costCenterId: '',
                    businessEventType: '',
                    currency: '',
                    hasClientDimensions: false
                  })}
                >
                  Clear Insurance Filters
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInsuranceFilters(prev => ({ 
                    ...prev, 
                    hasClientDimensions: !prev.hasClientDimensions 
                  }))}
                >
                  {insuranceFilters.hasClientDimensions ? 'Show All' : 'Insurance Only'}
                </Button>
              </div>
            </div>
          </div>

          <DataTable
            data={journals}
            columns={columns}
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Reversal Modal */}
      <Modal
        isOpen={reversalDialog.isOpen}
        onClose={() => {
          setReversalDialog({ isOpen: false, journalId: null, journalDescription: '' });
          setReversalReason('');
        }}
        title="Reverse Journal"
        description={`You are about to reverse the journal: "${reversalDialog.journalDescription}". This action cannot be undone. Please provide a reason for the reversal.`}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setReversalDialog({ isOpen: false, journalId: null, journalDescription: '' });
                setReversalReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReverseJournal}
              disabled={!reversalReason.trim() || isReversing}
            >
              {isReversing ? 'Reversing...' : 'Reverse Journal'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextArea
            label="Reason for Reversal"
            placeholder="e.g., Correction - duplicate entry posted in error"
            value={reversalReason}
            onChange={(e) => setReversalReason(e.target.value)}
            required
          />
        </div>
      </Modal>
    </div>
  );
}
